import React, { useState } from 'react';

// Hooks
import useCreateCustomer from '@src/hooks/useCreateCustomer';
import useCustomerLogin from '@src/hooks/useCustomerLogin';

// Comps
import {InputField, CustomFields} from '@project-stories/ecommerce/Account/AccountFields/AccountFields';
import ButtonSpinner from '@project-stories/elements/ButtonSpinner/ButtonSpinner';
import {Link} from '@arctheme-components/elements/Link/Link';
import {Paragraph} from '@project-stories/elements/Paragraph/Paragraph';
import {Checkbox} from "@project-stories/elements/Checkbox/Checkbox";
import useCustomerAccessToken from "@src/hooks/useCustomerAccessToken";

type Props = {
    section: any;
    showCustomFields?: boolean;
    signUpFields?: any;
};

const AccountRegisterForm = ({ section, showCustomFields, signUpFields }: Props) => {
    const {
        first_name_label,
        first_name_placeholder,
        last_name_label,
        last_name_placeholder,
        email_label,
        email_placeholder,
        password_label,
        password_placeholder,
        password_confirm_label,
        password_confirm_placeholder,
        button_label_register,
        button_style_register,
        button_label_already_registered
    } = section;

    // Sort custom fields
    const customSignUpFields = signUpFields.sort((a, b) => {
        const orderA = a.field_order ? parseInt(a.field_order) : Infinity;
        const orderB = b.field_order ? parseInt(b.field_order) : Infinity;
        return orderA - orderB;
    });

    // Hooks
    const {
        createCustomer,
        loading: createCustomerLoading,
        error: createCustomerError,
        success: createCustomerSuccess
    } = useCreateCustomer();
    const { loginCustomer, loading: loginLoading } = useCustomerLogin();
    const {
        getAccessToken,
        loading: accessLoading,
        errorMessage: accessError,
        successMessage: accessSuccess
    } = useCustomerAccessToken();

    // States
    const initialDependenciesState = Object.assign(
        {},
        {
            first_name: '',
            last_name: '',
            email: '',
            password: '',
            password_confirmation: ''
        },
        ...customSignUpFields.map((field) => ({
            [field.metafield_key]: ''
        }))
    );
    const initialDependenciesStateFilled = Object.assign(
        {},
        {
            first_name: false,
            last_name: false,
            email: false,
            password: false,
            password_confirmation: false
        },
        ...customSignUpFields.map((field) => ({
            [field.metafield_key]: field.is_required ? false : true
        }))
    );
    const initialDependenciesStateHasError = Object.assign(
        {},
        {
            first_name: false,
            last_name: false,
            email: false,
            password: false,
            password_confirmation: false
        },
        ...customSignUpFields.map((field) => ({
            [field.metafield_key]: false
        }))
    );
    const [dependenciesState, setDependenciesState]: any = useState(initialDependenciesState);
    const [dependenciesStateFilled, setDependenciesStateFilled]: any = useState(initialDependenciesStateFilled);
    const [dependenciesStateHasError, setDependenciesStateHasError]: any = useState(initialDependenciesStateHasError);

    // Handle input change and update global dependency state
    const handleInputChange = (e) => {
        const { name, value, type: inputType, checked } = e.target;

        // For checkboxes, use the checked value, otherwise use the input value
        const newValue = inputType === 'checkbox' ? checked : value;

        // Update the global dependency state
        updateDependencyState(name, newValue);

        // Check if the global dependency state is filled
        toggleDependencyStateFilled(name, newValue);
    };

    // Function to update the global dependencies state
    const updateDependencyState = (name, value) => {
        setDependenciesState((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    // Function to check if the global dependencies state has value
    const toggleDependencyStateFilled = (name, value) => {
        const customField = customSignUpFields.find((field) => name === field.metafield_key);

        setDependenciesStateFilled((prev) => ({
            ...prev,
            [name]: customField && !customField.is_required ? true : !!value
        }));
    };

    const resetErrors = () => {
        let errors = {};

        for (const field in dependenciesStateHasError) {
            errors = {
                ...errors,
                [field]: false
            };
        }

        setDependenciesStateHasError(errors);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        resetErrors();

        // Validate the fields
        let errors = {};
        for (const field in dependenciesStateFilled) {
            errors = {
                ...errors,
                [field]: !dependenciesStateFilled[field]
            };
        }

        for (const error in errors) {
            if (errors[error]) {
                setDependenciesStateHasError(errors);

                return;
            }
        }

        // Since this is newsletter, set optIn fields automatically
        setDependenciesState((prevState) => ({
            ...prevState,
            ['optInSMS']: true,
            ['optInEmail']: true
        }));

        const createResponse = await createCustomer(dependenciesState, customSignUpFields);
        if (createResponse) {
            const email = dependenciesState.email;
            const password = dependenciesState.password;
            // Check if the pair of email and password exists in the system
            const data: any = await getAccessToken(email, password);
            // If exists, then sign in
            if (data.success) {
                // Log the customer in after successful creation
                await loginCustomer(email, password);
            }
        }
    };

    return (
        <form
            id='registerForm'
            onSubmit={handleSubmit}
            className='account-page__form'
        >
            <div className='flex flex-col gap-y-4 items-center'>
                <div className={`w-full grid grid-cols-2`}>
                    <h4 className={`w-full text-left`}>PERSONAL INFORMATION</h4>
                    <p className={`b2 w-full text-right text-[#C0C0C0]`}>*Required field</p>
                </div>
                {/* First Name */}
                <InputField
                    className={`h4 w-full`}
                    inputType='text'
                    id='firstNameRegister'
                    name='first_name'
                    value={dependenciesState['first_name']}
                    onChange={handleInputChange}
                    required={true}
                    label={first_name_label}
                    placeholder={`${first_name_placeholder}*`}
                    hasError={dependenciesStateHasError['first_name']}
                    errorMessage={`${first_name_label} is required`}
                />

                {/* Last Name */}
                <InputField
                    className={`h4 w-full`}
                    inputType='text'
                    id='lastNameRegister'
                    name='last_name'
                    value={dependenciesState['last_name']}
                    onChange={handleInputChange}
                    required={true}
                    label={last_name_label}
                    placeholder={`${last_name_placeholder}*`}
                    hasError={dependenciesStateHasError['last_name']}
                    errorMessage={`${last_name_label} is required`}
                />

                {/* Email */}
                <InputField
                    className={`h4 w-full`}
                    inputType='email'
                    id='emailRegister'
                    name='email'
                    value={dependenciesState['email']}
                    onChange={handleInputChange}
                    required={true}
                    label={email_label}
                    placeholder={`${email_placeholder}*`}
                    hasError={dependenciesStateHasError['email']}
                    errorMessage={`${email_label} is required`}
                />

                {/* Password */}
                <InputField
                    className={`h4 w-full`}
                    inputType='password'
                    id='passwordRegister'
                    name='password'
                    value={dependenciesState['password']}
                    onChange={handleInputChange}
                    required={true}
                    label={password_label}
                    placeholder={`${password_placeholder}*`}
                    hasError={dependenciesStateHasError['password']}
                    errorMessage={`${password_label} is required`}
                />

                {/* Confirm Password */}
                <InputField
                    className={`h4 w-full`}
                    inputType='password'
                    id='passwordConfirmationRegister'
                    name='password_confirmation'
                    value={dependenciesState['password_confirmation']}
                    onChange={handleInputChange}
                    required={true}
                    label={password_confirm_label}
                    placeholder={`${password_confirm_placeholder}*`}
                    hasError={dependenciesStateHasError['password_confirmation']}
                    errorMessage={`${password_confirm_label} is required`}
                />

                {/* Custom Fields */}
                {showCustomFields && (
                    <CustomFields
                        customSignUpFields={customSignUpFields}
                        onChange={handleInputChange}
                        values={dependenciesState}
                        valuesError={dependenciesStateHasError}
                    />
                )}

                {createCustomerError && (
                    <Paragraph
                        type='errors text-center'
                        text={createCustomerError}
                    />
                )}

                <ButtonSpinner
                    classes={`w-full button button-${button_style_register} h-[calc(3rem-2px)] text-base`}
                    isLoading={createCustomerLoading || loginLoading}
                    loadingText={'Creating account...'}
                    ariaLabel='Submit registration form'
                >
                    {button_label_register}
                </ButtonSpinner>

                <Checkbox label={`Sign up for newsletter`} />
                <p className={`b4 account-page__form-terms`}>By selecting to join our mailing list you agree to our privacy policy.</p>

                <hr />
                <Link
                    link='/account/login'
                    classes='b4 link--underlined text-[#696969] hidden'
                    ariaLabel='Go to login page'
                >
                    {button_label_already_registered}
                </Link>

                {/* Social login app snippet */}
                <div className='one-click-social-login'></div>
            </div>
        </form>
    );
};

export default AccountRegisterForm;
